import { useMemo } from 'react';
import { fontFamily } from '@podiumhq/podium-ui';
import { useTracking } from '@podiumhq/tracking';

import { useCreditCardContext } from 'contexts/CreditCardDetailsContextProvider';
import { stripeErrorMessage } from 'lib/creditCardValidators';
import {
  getErrorMessage,
  getTargetValue,
  isPostalCode,
} from 'lib/fieldValidation';

import { CANADIAN_COUNTRY_CODE, US_COUNTRY_CODE } from '../../BusinessDetails';
import { FORM_COLORS } from '../../FormElements';

import {
  CARD_ID,
  EXPIRATION_DATE_ID,
  POSTAL_CODE_ID,
  SECURITY_CODE_ID,
} from '../constants';

const useCreditCardFields = (loading, trackOnBlurPath) => {
  const { trackEvent } = useTracking();
  const {
    creditCardFields = {},
    setZip,
    updateCreditCardField,
  } = useCreditCardContext() || {};

  const clearFieldError = (fieldName) =>
    updateCreditCardField &&
    updateCreditCardField(fieldName, {
      ...creditCardFields[fieldName],
      error: null,
    });

  const updateField = ({ fieldName, ...props }) =>
    updateCreditCardField &&
    updateCreditCardField(fieldName, {
      ...creditCardFields[fieldName],
      ...props,
    });

  const handleStripeInputChange =
    (fieldName) =>
    (event = {}) => {
      clearFieldError(fieldName);

      const { elementType, empty, target } = event;
      const isEmpty = elementType ? empty : !target?.value;

      updateField({
        fieldName,
        error: isEmpty ? null : stripeErrorMessage(event?.error),
        isEmpty,
      });
    };

  const handleZipBlur = (event) => {
    const fieldName = POSTAL_CODE_ID;
    const postalCode = getTargetValue(event);

    if (!postalCode) {
      return updateField({
        fieldName,
        error: getErrorMessage('zip', postalCode),
        isEmpty: true,
      });
    }

    const isValid =
      isPostalCode(postalCode, US_COUNTRY_CODE) ||
      isPostalCode(postalCode, CANADIAN_COUNTRY_CODE);

    if (isValid) {
      setZip(postalCode);
      updateField({
        fieldName,
        error: null,
        isEmpty: false,
      });
    } else {
      updateField({
        fieldName,
        error: getErrorMessage('zip', postalCode),
        isEmpty: false,
      });
    }

    trackOnBlurPath && trackEvent(`${trackOnBlurPath}.postalCodeFieldFilled`);
  };

  const handleZipChange = (event) => {
    const fieldName = POSTAL_CODE_ID;
    clearFieldError(fieldName);

    setZip(getTargetValue(event));
  };

  const handleStripeInputBlur = (fieldName) => () =>
    trackOnBlurPath && trackEvent(`${trackOnBlurPath}.${fieldName}FieldFilled`);

  const stripeInputOptions = {
    style: {
      base: {
        color: FORM_COLORS.textColor,
        fontFamily: fontFamily,
        fontSize: '17px',
        fontWeight: 400,
        lineHeight: '38px',

        '::placeholder': {
          color: FORM_COLORS.placeholderText,
        },
      },
      invalid: {
        color: FORM_COLORS.textColor,
      },
    },
  };

  const inputPropsWithPlaceholder = (placeholder, props, stripeProps = null) =>
    placeholder?.length > 1
      ? {
          ...props,
          options: {
            ...stripeInputOptions,
            placeholder,
          },
        }
      : {
          ...props,
          options: stripeProps
            ? { ...stripeInputOptions, ...stripeProps }
            : stripeInputOptions,
        };

  const fieldProps = useMemo(
    () => {
      const className = '.fs-mask';
      const disabled = loading;

      const sharedInputProps = { className, disabled };

      return {
        [CARD_ID]: {
          autoComplete: 'cc-number',
          error: creditCardFields[CARD_ID]?.error,
          id: CARD_ID,
          onBlur: handleStripeInputBlur('cardNumber'),
          onChange: handleStripeInputChange(CARD_ID),
          placeholder: 'Card Number',
          ...inputPropsWithPlaceholder('Card number', sharedInputProps, {
            showIcon: true,
          }),
        },
        [EXPIRATION_DATE_ID]: {
          autoComplete: 'cc-exp',
          error: creditCardFields[EXPIRATION_DATE_ID]?.error,
          id: EXPIRATION_DATE_ID,
          onBlur: handleStripeInputBlur('cardExpiration'),
          onChange: handleStripeInputChange(EXPIRATION_DATE_ID),
          placeholder: 'Expiration Date',
          ...inputPropsWithPlaceholder(
            'Expiration date MM/YY',
            sharedInputProps
          ),
        },
        [SECURITY_CODE_ID]: {
          error: creditCardFields[SECURITY_CODE_ID]?.error,
          id: SECURITY_CODE_ID,
          onBlur: handleStripeInputBlur('cardSecurityCode'),
          onChange: handleStripeInputChange(SECURITY_CODE_ID),
          placeholder: 'Security Code',
          ...inputPropsWithPlaceholder('Security code', sharedInputProps),
        },
        [POSTAL_CODE_ID]: {
          sharedInputProps,
          autoComplete: 'postal-code',
          error: creditCardFields[POSTAL_CODE_ID]?.error,
          id: POSTAL_CODE_ID,
          onBlur: handleZipBlur,
          onChange: handleZipChange,
          placeholder: 'Billing Postal Code',
          variant: 'outline',
        },
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [creditCardFields, loading]
  );

  return {
    fieldProps,
    handleStripeInputBlur,
    handleStripeInputChange,
    handleZipBlur,
    handleZipChange,
  };
};

export default useCreditCardFields;
